import { Injectable, Type } from '@angular/core';
import { ActionSheetButton, ActionSheetController } from '@ionic/angular';
import { UnitActivity } from '@models/imported/SagaSchema/UnitActivity';
import { TranslateService } from '@ngx-translate/core';
import { UnitActivityProxyService } from '@services/unit-activity-proxy/unit-activity-proxy.service';
import { UnitActivityService } from '@services/unit-activity/unit-activity.service';
import { Observable, Subject, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { UnitActivityEditorComponent } from '../../components/unit-activity-editor/unit-activity-editor.component';

type SelectFunction = (name: string) => Observable<any>;
export interface IUnitSelector {
  create?: Type<any>;
  select?: SelectFunction;
  disableDissociate?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UnitSelectorService {
  private _deviceIdentifier: string = null;

  private _result: Subject<UnitActivity> = null;
  private _htmlIAS: HTMLIonActionSheetElement = null;

  private _selector: IUnitSelector = null;

  unitCreationButtons: ActionSheetButton[] = [];

  get disableDissociate() {
    return this._selector ? this._selector.disableDissociate : true;
  }

  constructor(
    private actionSheetController: ActionSheetController,
    private translate: TranslateService,
    private unitActivityService: UnitActivityService,
    private unitActivityProxy: UnitActivityProxyService
  ) {}

  useCustomUnitSelector(selector: IUnitSelector) {
    this._selector = selector;
  }

  setDeviceId(deviceIdentifier: string) {
    this._deviceIdentifier = deviceIdentifier;
  }

  addUnitCreationButton(button: ActionSheetButton) {
    if (this.unitCreationButtons.indexOf(button) == -1) {
      this.unitCreationButtons.push(button);
    }
  }

  removeUnitCreationButton(button: ActionSheetButton) {
    const index = this.unitCreationButtons.indexOf(button);
    if (index !== -1) {
      this.unitCreationButtons.splice(index, 1);
    }
  }

  createButton(text: string, handler?): ActionSheetButton {
    return {
      text: this.translate.instant(text),
      handler: handler
    };
  }

  reset() {
    this._deviceIdentifier = null;

    if (this._result !== null) {
      this._result = null;
      this._htmlIAS.dismiss().then(() => (this._htmlIAS = null));
    }

    this.unitCreationButtons.length = 0;
  }

  showActionSheet(): Observable<UnitActivity> {
    if (this._result === null) {
      this._result = new Subject<UnitActivity>();

      return from(
        this.actionSheetController
          .create({
            header: this.translate.instant('static.login.unit.nounit'),
            buttons: this.createActionSheetButtons()
          })
          .then(ionActionSheet => (this._htmlIAS = ionActionSheet).present())
      ).pipe(switchMap(() => this._result.asObservable()));
    } else {
      return this._result;
    }
  }

  private createActionSheetButtons(): ActionSheetButton[] {
    const buttons: ActionSheetButton[] = [];
    buttons.push(this.createButton('static.login.unit.Create', () => this.onCreate()));

    if (this._selector && this._selector.select) {
      buttons.push(this.createButton('static.login.unit.Select', () => this.onSelect()));
    }

    if (buttons.length > 0) {
      const cancelButton = this.createButton('static.login.unit.cancel', () => this.onCancel());
      cancelButton.role = 'cancel';
      buttons.push(cancelButton);
    }

    return buttons;
  }

  private onCancel() {
    this._result = null;
  }

  onCreate(): Promise<boolean> {
    if (!this._selector || !this._selector.create) {
      return this.unitActivityProxy.createUnitActivity({}).toPromise();
    }

    const component = this.getCreateComponent();
    return this.unitActivityService.openCreateUnit(component, { identifier: this._deviceIdentifier }).then(unitActivity => {
      // when unit was truthy, associate was called here
      if (this._htmlIAS !== null) {
        this._htmlIAS.dismiss().then(() => {
          this._result.next(unitActivity);
          if (!unitActivity) {
            this.onCancel();
          }
        });
      }
      return unitActivity ? true : false;
    });
  }

  getCreateComponent() {
    return this._selector && this._selector.create ? this._selector.create : UnitActivityEditorComponent;
  }

  private onSelect(): boolean {
    this._selector.select(this._deviceIdentifier).subscribe(unitActivity => {
      if (this._htmlIAS !== null) {
        this._htmlIAS.dismiss().then(() => {
          this._result.next(unitActivity);
          if (!unitActivity) {
            this.onCancel();
          }
        });
      }
    });

    return true;
  }
}
